import { useCallback, useRef } from 'react'
import type { CV } from '../types/cv'

const MAX_HISTORY = 50
const SNAPSHOT_DEBOUNCE_MS = 2000

interface HistoryState {
  past: CV[]
  future: CV[]
}

export function useHistory() {
  const historyRef = useRef<HistoryState>({ past: [], future: [] })
  const lastSnapshotTime = useRef<number>(0)

  const pushSnapshot = useCallback((cv: CV) => {
    const now = Date.now()
    // Debounce snapshots: don't push if too recent
    if (now - lastSnapshotTime.current < SNAPSHOT_DEBOUNCE_MS) return
    lastSnapshotTime.current = now

    const history = historyRef.current
    const newPast = [...history.past, structuredClone(cv)]
    if (newPast.length > MAX_HISTORY) {
      newPast.shift()
    }
    historyRef.current = {
      past: newPast,
      future: [], // Clear redo stack on new change
    }
  }, [])

  const undo = useCallback((currentCV: CV): CV | null => {
    const history = historyRef.current
    if (history.past.length === 0) return null

    const previous = history.past[history.past.length - 1]
    historyRef.current = {
      past: history.past.slice(0, -1),
      future: [structuredClone(currentCV), ...history.future],
    }
    return previous
  }, [])

  const redo = useCallback((currentCV: CV): CV | null => {
    const history = historyRef.current
    if (history.future.length === 0) return null

    const next = history.future[0]
    historyRef.current = {
      past: [...history.past, structuredClone(currentCV)],
      future: history.future.slice(1),
    }
    return next
  }, [])

  const canUndo = useCallback(() => historyRef.current.past.length > 0, [])
  const canRedo = useCallback(() => historyRef.current.future.length > 0, [])

  const clear = useCallback(() => {
    historyRef.current = { past: [], future: [] }
    lastSnapshotTime.current = 0
  }, [])

  return { pushSnapshot, undo, redo, canUndo, canRedo, clear }
}
